import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

/**
 * Prevent other components from being focused while this is rendered
 */
export const FocusLock = () => {
  const divRef = useRef<HTMLDivElement>(null);
  const initiallyFocusedThing = useMemo<HTMLElement | undefined>(() => {
    if (document.activeElement && 'focus' in document.activeElement) {
      return document.activeElement;
    }
  }, []);
  const lastThingToGetFocus = useRef<HTMLElement | undefined>(initiallyFocusedThing);

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      // relatedTarget is the thing that was attempted
      // to be focused
      const focusedTo = e.relatedTarget;
      if (focusedTo && 'focus' in focusedTo) {
        lastThingToGetFocus.current = focusedTo;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      // Do not allow the new focus, instead, re-focus the lock-div
      divRef.current?.focus();
    };
    // Keep focus on this lock-div while the lock is enabled
    divRef.current?.focus();
    window.addEventListener('focusout', handleFocus);
    return () => {
      window.removeEventListener('focusout', handleFocus);
      // Re-focus the last thing. This ensures that new auto-focused components that came into
      // existance while we were focus-locked will get focus back when we unlock
      lastThingToGetFocus.current?.focus();
    };
  }, []);

  return <LockDiv data-testid="focus-lock" ref={divRef} tabIndex={0} />;
};

const LockDiv = styled.div`
  /* Keep it in the viewport so when we focus, we don't affect the page-scroll position */
  position: fixed;
  top: 0px;
  left: 0px;
  opacity: 0;
`;
